import { useState, useEffect } from 'react';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { ArrowLeft, File, Clock, Folder, Search, Code } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Editor = ({ user }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [recentChanges, setRecentChanges] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('recent');
  const [fileStats, setFileStats] = useState({});        // file path -> { lines, size }
  const [statsTotals, setStatsTotals] = useState(null);  // { total_files, total_lines, total_size }
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    loadFiles();
    loadRecentChanges();
  }, []);

  const loadFiles = async () => {
    try {
      const response = await api.get(`${API}/editor/files`);
      setFiles(response.data.files);
      loadFileStats(response.data.files);
    } catch (error) {
      console.error('Error loading files:', error);
      toast.error('Failed to load files');
    }
  };

  // Line count + size for every file in the list, and the total lines of code across all of them.
  const loadFileStats = async (fileList) => {
    const paths = (fileList || []).map((f) => f.path).filter(Boolean);
    if (paths.length === 0) return;
    setStatsLoading(true);
    try {
      const response = await api.post(`${API}/editor/file-stats`, { paths });
      setFileStats(response.data.files || {});
      setStatsTotals(response.data.totals || null);
    } catch (error) {
      console.error('Error loading file stats:', error);
      toast.error('Failed to load line counts');
    } finally {
      setStatsLoading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const loadRecentChanges = async () => {
    try {
      const response = await api.get(`${API}/editor/recent-changes`);
      setRecentChanges(response.data.changes);
    } catch (error) {
      console.error('Error loading recent changes:', error);
      toast.error('Failed to load recent changes');
    }
  };

  const loadFileContent = async (filePath) => {
    setLoading(true);
    try {
      const response = await api.get(`${API}/editor/file`, {
        params: { file_path: filePath }
      });
      setFileContent(response.data.content);
      setSelectedFile(filePath);
    } catch (error) {
      console.error('Error loading file:', error);
      toast.error('Failed to load file content');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (type) => {
    const colors = {
      'py': 'text-blue-500',
      'js': 'text-yellow-500',
      'jsx': 'text-cyan-500',
      'ts': 'text-blue-600',
      'tsx': 'text-cyan-600',
      'json': 'text-green-500',
      'css': 'text-pink-500',
      'html': 'text-orange-500',
      'env': 'text-gray-500'
    };
    return colors[type] || 'text-gray-400';
  };

  const filteredFiles = files.filter(file =>
    file.relative_path.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>Only super admins can access the code editor</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full gap-2"
              style={{ backgroundColor: '#ee7203', color: 'white' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/')} 
                data-testid="back-button"
                className="gap-2"
                style={{ borderColor: '#ee7203', color: '#ee7203' }}
              >
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1d1d1b' }} data-testid="editor-title">
                  Code Editor
                </h1>
                <p className="text-sm text-gray-500 mt-1">View and browse application code</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - File Browser */}
          <div className="lg:col-span-1">
            <Card className="h-[calc(100vh-12rem)] flex flex-col">
              <CardHeader>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="recent" className="gap-2">
                      <Clock className="w-4 h-4" />
                      Recent
                    </TabsTrigger>
                    <TabsTrigger value="all" className="gap-2">
                      <Folder className="w-4 h-4" />
                      All Files
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                {activeTab === 'all' && (
                  <div className="mt-4">
                    <div className="mb-3 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-900" data-testid="editor-totals">
                      {statsLoading ? 'Counting lines…' : statsTotals ? (
                        <>
                          <span className="font-bold">{statsTotals.total_lines.toLocaleString()}</span> lines of code across{' '}
                          <span className="font-bold">{statsTotals.total_files.toLocaleString()}</span> files ·{' '}
                          <span className="font-bold">{formatSize(statsTotals.total_size)}</span> total
                        </>
                      ) : 'Line counts unavailable'}
                    </div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search files..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto">
                {activeTab === 'recent' ? (
                  <div className="space-y-2">
                    {recentChanges.map((file, index) => (
                      <button
                        key={index}
                        onClick={() => loadFileContent(file.path)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedFile === file.path
                            ? 'bg-purple-50 border-purple-300'
                            : 'hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <File className={`w-4 h-4 ${getFileIcon(file.type)}`} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{file.name}</div>
                            <div className="text-xs text-gray-500 truncate">{file.relative_path}</div>
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">{file.modified}</div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredFiles.map((file, index) => (
                      <button
                        key={index}
                        onClick={() => loadFileContent(file.path)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedFile === file.path
                            ? 'bg-purple-50 border-purple-300'
                            : 'hover:bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <File className={`w-4 h-4 ${getFileIcon(file.type)}`} />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{file.name}</div>
                            <div className="text-xs text-gray-500 truncate">{file.relative_path}</div>
                          </div>
                        </div>
                        {fileStats[file.path] && (
                          <div className="text-xs text-gray-400 mt-1 pl-6" data-testid="editor-file-stats">
                            {fileStats[file.path].lines != null ? `${fileStats[file.path].lines.toLocaleString()} lines` : '— lines'} · {formatSize(fileStats[file.path].size)}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Code Viewer */}
          <div className="lg:col-span-2">
            <Card className="h-[calc(100vh-12rem)] flex flex-col">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Code className="w-5 h-5 text-purple-600" />
                    <CardTitle className="text-lg">
                      {selectedFile ? selectedFile.replace('/app/', '') : 'Select a file to view'}
                    </CardTitle>
                  </div>
                  {selectedFile && (
                    <div className="text-sm text-gray-500">
                      {(fileStats[selectedFile]?.lines ?? fileContent.split('\n').length).toLocaleString()} lines{fileStats[selectedFile]?.size != null ? ` · ${formatSize(fileStats[selectedFile].size)}` : ''}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-hidden">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-500">Loading...</div>
                  </div>
                ) : selectedFile ? (
                  <div className="h-full overflow-auto">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm font-mono leading-relaxed h-full overflow-auto">
                      <code>{fileContent}</code>
                    </pre>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    <Code className="w-16 h-16 mb-4" />
                    <p className="text-lg">Select a file from the left panel to view its contents</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Copyright Notice - Full Width */}
        <div className="max-w-full mx-auto mt-6 px-4">
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg border-2 border-purple-200">
            <h2 className="text-xl font-bold text-purple-800 mb-3">
              Copyright & Intellectual Property Notice
            </h2>
            <p className="text-sm text-gray-700 mb-2">
              <strong>Developer & Owner:</strong> Jamie Moseley (moseley_jj@hotmail.com)
            </p>
            
            <div className="mt-4 p-4 bg-white rounded border border-purple-300">
              <h3 className="font-semibold text-gray-800 mb-2">Complete Application Copyright</h3>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                All code, functionality, features, designs, user interfaces, workflows, database schemas, API endpoints, 
                algorithms, and technical implementations contained within this C30 Transport Log application and all 
                associated systems are the exclusive intellectual property of <strong>Jamie Moseley</strong>.
              </p>
              
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                This includes but is not limited to: frontend components (React.js), backend services (FastAPI), database 
                structures (MongoDB), photo management systems, backup systems, authentication mechanisms, reporting features, 
                user management, file management, mileage tracking, areas mapping, and all other integrated functionality.
              </p>

              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                <strong>Ideas & Concepts:</strong> All original ideas, concepts, and business logic related to this 
                application have been conceived and developed by Jamie Moseley since <strong>12th May 2018</strong> and 
                remain protected intellectual property.
              </p>

              <p className="text-sm text-gray-700 leading-relaxed mb-3 bg-purple-50 p-3 rounded border border-purple-200">
                <strong>Development Context & Patent Rights:</strong> This system has been developed primarily during the 
                developer's own time, with testing and development conducted at scheduled points during the working week 
                as explicitly agreed with the Branch Director (line manager). Under employment contract clause 18.1 (Patent Rights), 
                where the employing company has not expressed direct interest in acquiring ownership, the developer retains 
                full intellectual property rights and may take steps to secure and protect their own interests.
              </p>

              <p className="text-sm text-gray-700 leading-relaxed mb-3 bg-orange-50 p-3 rounded border border-orange-200">
                <strong>Usage Rights & Financial Terms:</strong> Use of this system is permitted only as explicitly agreed 
                by Jamie Moseley. Protection of intellectual property interests will be instantly chargeable up to any amount 
                specified by the developer for the entire duration of use. All charges must comprehensively cover previous 
                development costs (since 12th May 2018) and future costs, unless a full sale agreement is reached between 
                the developer and the employing company.
              </p>
              
              <p className="text-sm text-gray-700 leading-relaxed">
                <strong>Legal Protection:</strong> This code, in its entirety or any portion thereof, may not be copied, 
                reproduced, distributed, modified, reverse-engineered, decompiled, or used in any form—in whole or in part—without 
                explicit written permission from the copyright holder. Unauthorized use, replication, or distribution is strictly 
                prohibited and may result in legal action under applicable copyright, patent, and intellectual property laws 
                of England and Wales, United Kingdom.
              </p>
              
              <p className="text-xs text-gray-600 mt-4 pt-3 border-t border-purple-200">
                <strong>Complete Copyright:</strong> © 12th May 2018 - Present, Jamie Moseley. All rights reserved.<br />
                <strong>Coverage:</strong> All code, implementation, ideas, concepts, and intellectual property.<br />
                <strong>Full Legal Agreement:</strong> <a href="/terms-conditions.html" target="_blank" className="text-purple-600 underline hover:text-purple-800">View Terms & Conditions (includes Copyright & Patent Agreement)</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Editor;
